import { Component, computed, effect, inject, input, signal, untracked, ElementRef, ViewChild } from '@angular/core';
import { RouterLink } from '@angular/router';
import { AuditLog } from '../../core/models/service/audit-log.model';
import { AuditEntityType } from '../../core/enums/base.enums';
import { UserPermission } from '../../core/enums/permissions.enums';
import { UserService } from '../../core/services/user.service';
import { OrganizationService } from '../../core/services/organization.service';
import { MemberService } from '../../core/services/member.service';
import { PropertiesService } from '../../core/services/property.service';
import { ChargesService } from '../../core/services/charge/charge.service';
import { FinancialService } from '../../core/services/financial/financial.service';
import { ChartOfAccountService } from '../../core/services/chart-of-account/chart-of-account.service';
import { DocumentService } from '../../core/services/document.service';
import { BankConnectionService } from '../../core/services/bank-connection/bank-connection.service';
import { AuthorizationService } from '../../core/services/authorization/authorization.service';
import { AuthorizationStateService } from '../../core/services/authorization/authorization-state.service';
import { UserSessionService } from '../../core/services/user-session.service';
import { ProfileOrganizationScopeService } from '../../core/services/authorization/profile-organization-scope.service';
import { DecimalPipe } from '@angular/common';
import { OrgDatePipe } from '../../core/pipes/org-date/org-date.pipe';
import { FormattedAuditLog } from '../../core/models/ui/formatted-audit-message.model';
import { CommonChecks } from '../../core/utilities/common-checks.utilities';
import { PermissionUtilities } from '../../core/utilities/permission.utilities';
import { FaDuotoneIconComponent } from '@fortawesome/angular-fontawesome';
import { PaginationComponent } from '../pagination/pagination.component';
import { TooltipDirective } from '../../core/directives/tooltip.directive';
import { PaginationConstants } from '../../core/constants/constants';
import { PaginationUtilities } from '../../core/utilities/pagination.utilities';
import { PaginatedListModel } from '../../core/models/service/paginated-list.model';
import { SafeHtmlPipe } from "../../core/pipes/safe-html/safe-html.pipe";
import { AuditLogFormatter } from '../../core/utilities/audit-log-formatter.utilities';

@Component({
  selector: 'ms-audit-logs',
  imports: [OrgDatePipe, DecimalPipe, FaDuotoneIconComponent, PaginationComponent, SafeHtmlPipe, RouterLink, TooltipDirective],
  templateUrl: './audit-logs.component.html',
  styleUrl: './audit-logs.component.scss'
})
export class AuditLogsComponent {
  private authorizationService = inject(AuthorizationService);
  private authorizationStateService = inject(AuthorizationStateService);
  private userSessionService = inject(UserSessionService);
  private profileScope = inject(ProfileOrganizationScopeService, { optional: true });

  // The organization whose logs are on screen: the profile entity's own when rendered on an
  // organization, member or property profile, otherwise the active one. Those pages are reachable
  // for any organization the viewer belongs to without changing the active one (DEV-2643). Held as
  // the whole scope rather than just the id so the checks below can fail closed while it is pending,
  // instead of an undefined id standing in for the active organization or for any of them.
  private organizationScope = computed(() =>
    PermissionUtilities.resolveOrganizationScope(undefined, this.profileScope, this.userSessionService));

  public auditLogs = signal<AuditLog[]>([]);
  public formattedAuditLogs = signal<FormattedAuditLog[]>([]);
  public entityId = input<number>();
  public entityType = input<AuditEntityType>();
  public expandedRows = signal<Set<number>>(new Set());

  public canViewDetails = computed(() => PermissionUtilities.canViewAuditLogDetails(
    this.authorizationService,
    this.authorizationStateService,
    this.userSessionService,
    this.entityType(),
    this.organizationScope()
  ));

  public canViewUsers = computed(() => {
    const authState = this.authorizationStateService.authorizationState();
    if (!authState) return false;
    return PermissionUtilities.hasScopedOrganizationPermission(
      UserPermission.Read, authState, this.authorizationService, this.profileScope, this.userSessionService);
  });

  // Pagination
  public skip = signal(0);
  private listKey: string = PaginationUtilities.getListKey(inject(ElementRef).nativeElement);
  public take: number = PaginationUtilities.getViewportPageSize(this.listKey);
  public totalCount: number = 0;
  public hasNext: boolean = false;
  public canLoadMore: boolean = true;

  // Loading state
  public isLoading: boolean = true;
  public skeletonLogs: FormattedAuditLog[] = [];

  @ViewChild(PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_NAME().replace(/^#/, '')) jumpToElementRef!: ElementRef;
  @ViewChild(PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_MOBILE_NAME().replace(/^#/, '')) jumpToElementMobileRef!: ElementRef;

  constructor(
    private userService: UserService,
    private propertiesService: PropertiesService,
    private memberService: MemberService,
    private organizationService: OrganizationService,
    private chargesService: ChargesService,
    private financialService: FinancialService,
    private chartOfAccountService: ChartOfAccountService,
    private documentService: DocumentService,
    private bankConnectionService: BankConnectionService
  ) {
    effect(() => {
      const id = this.entityId();
      const type = this.entityType();
      const authState = this.authorizationStateService.authorizationState();
      // Reload from scratch only when the entity or auth state changes. The load reads/writes the
      // `skip` signal (two-way bound to ms-pagination), so it must run untracked — otherwise each
      // "load more" skip bump would re-fire this effect and reset pagination back to the first page.
      if (id != null && type != null && authState !== undefined) {
        untracked(() => this.loadAuditLogs());
      }
    });
  }

  public ngOnInit(): void {
    this.createSkeletonLogs();
  }

  private createSkeletonLogs(): void {
    this.skeletonLogs = Array.from({ length: this.take }, (_, index) => ({
      timestamp: new Date(Date.now() + index).toISOString(),
      messageTitle: 'Loading',
      messageDetails: [],
      note: undefined
    } as FormattedAuditLog));
  }

  public get displayLogs(): FormattedAuditLog[] {
    const formattedLogsValue = this.formattedAuditLogs();
    if (this.isLoading && (!formattedLogsValue || formattedLogsValue.length === 0)) {
      return this.skeletonLogs;
    }
    return formattedLogsValue || [];
  }

  public async loadAuditLogs(append: boolean = false): Promise<void> {
    this.isLoading = true;

    if (append === false) {
      this.resetPagination();
    }

    if (this.entityId() != null && this.entityType()) {
      let response: PaginatedListModel<AuditLog> | null = null;
      const includeDetails = this.canViewDetails();

      switch (this.entityType()) {
        case AuditEntityType.User:
          response = (await this.userService.getAuditLogsByUserId(this.entityId()!, this.skip(), this.take, includeDetails)).data ?? null;
          break;
        case AuditEntityType.Property:
          response = (await this.propertiesService.getAuditLogsByPropertyId(this.entityId()!, this.skip(), this.take, includeDetails)).data ?? null;
          break;
        case AuditEntityType.Member:
          response = (await this.memberService.getAuditLogsByMemberId(this.entityId()!, this.skip(), this.take, includeDetails)).data ?? null;
          break;
        case AuditEntityType.Organization:
          response = (await this.organizationService.getAuditLogsByOrganizationId(this.entityId()!, this.skip(), this.take, includeDetails)).data ?? null;
          break;
        case AuditEntityType.PropertyChargeSchedule:
          response = (await this.chargesService.getAuditLogsByPropertyChargeScheduleId(this.entityId()!, this.skip(), this.take, includeDetails)).data ?? null;
          break;
        case AuditEntityType.PropertyCharge:
          response = (await this.chargesService.getAuditLogsByPropertyChargeId(this.entityId()!, this.skip(), this.take, includeDetails)).data ?? null;
          break;
        case AuditEntityType.Budget:
          response = (await this.financialService.getAuditLogsByBudgetId(this.entityId()!, this.skip(), this.take, includeDetails)).data ?? null;
          break;
        case AuditEntityType.ChartOfAccount:
          // Organization-scoped, not per-account — the caller passes the organization id as entityId.
          response = (await this.chartOfAccountService.getChartOfAccountAuditLogs(this.entityId()!, this.skip(), this.take, includeDetails)).data ?? null;
          break;
        case AuditEntityType.BankConnection:
          // Per connected account, and the caller passes that connection id as the entity id.
          response = (await this.bankConnectionService.getBankConnectionLogs(this.entityId()!, this.skip(), this.take, includeDetails)).data ?? null;
          break;
        case AuditEntityType.DocumentCenter:
          response = (await this.documentService.getDocumentCenterAuditLogs(this.entityId()!, this.skip(), this.take, includeDetails)).data ?? null;
          break;
        default:
          console.error('Unknown entity type for audit logs');
          this.isLoading = false;
          return;
      }

      if (response) {
        this.hasNext = response.hasNext ?? false;
        this.totalCount = response.total || 0;

        if (append) {
          this.auditLogs.set(this.auditLogs().concat(response.data || []));
        } else {
          this.auditLogs.set(response.data || []);
        }
        
        this.formatMessages(this.auditLogs());
      }
    }

    this.isLoading = false;
  }

  private formatMessages(auditLogs: AuditLog[]): void {
    this.formattedAuditLogs.set(auditLogs.map(log => AuditLogFormatter.format(log)));
  }

  public toggleRow(index: number): void {
    const expanded = new Set(this.expandedRows());
    if (expanded.has(index)) {
      expanded.delete(index);
    } else {
      expanded.add(index);
    }
    this.expandedRows.set(expanded);
  }

  public isRowExpandable(log: FormattedAuditLog): boolean {
    if (log.messageDetails && log.messageDetails.length > 0) {
      return true;
    }

    if (this.canViewDetails()) {
      return !CommonChecks.isNullOrUndefinedOrEmpty(log.note);
    }

    return false;
  }

  //#region Pagination
  public async loadMore(): Promise<void> {
    this.loadAuditLogs(true);
  }

  public async loadMoreDebounce(): Promise<void> {
    setTimeout(() => {
      this.canLoadMore = true;
    }, PaginationConstants.LOAD_MORE_DEBOUNCE_TIME_MILLISECONDS());
  }

  public resetPagination(): void {
    this.take = PaginationUtilities.getViewportPageSize(this.listKey);
    this.skip.set(0);
  }

  public jumpToTop(element: string): void {
    switch (element) {
      case PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_NAME():
        this.jumpToElementRef.nativeElement.scrollIntoView({ behavior: 'smooth' });
        break;
      case PaginationConstants.DEFAULT_SCROLL_TO_ELEMENT_MOBILE_NAME():
        this.jumpToElementMobileRef.nativeElement.scrollIntoView({ behavior: 'smooth' });
        break;
    }
  }

  public loadMoreInView(): void {
    if (this.canLoadMore && this.hasNext) {
      this.loadMore();
      this.canLoadMore = false;
      this.loadMoreDebounce();
    }
  }
}
